import { createSlice } from "@reduxjs/toolkit";

interface MeetingState {
  studentModules: any[];
  subModules: any[];
  meetingExerciseViews: any[];
  exerciseTabs: Array<{
    id: string;
    title: string;
    favicon: string;
    active: boolean;
  }>;
  loading: boolean;
  subModulesLoading: boolean;
  creatingExercise: boolean;
  exerciseViewsLoading: boolean;
  error: string | null;
  subModulesError: string | null;
  exerciseError: string | null;
  exerciseViewsError: string | null;
  viewResultExerciseId: string | null;
  viewResultExerciseName: string | null;
  exerciseActivityData: any[];
  exerciseActivityLoading: boolean;
  exerciseActivityError: string | null;
  exportExerciseActivityLoading: boolean;
  edxDocumentBlockId: string | null;
  edxDocumentName: string | null;
  studentAnswerExerciseQuestion: any;
  studentAnswerExerciseQuestionLoading: boolean;
  studentAnswerExerciseQuestionError: string | null;
  meetingSessionSubmoduleResult: any;
  exerciseSessionData: any;
  exerciseSessionLoading: boolean;
  exerciseSessionError: string | null;
  selectedExercise: {
    id: string | number | null;
    data: any;
  };
}

const initialState: MeetingState = {
  studentModules: [],
  subModules: [],
  meetingExerciseViews: [],
  exerciseTabs: [],
  loading: false,
  subModulesLoading: false,
  creatingExercise: false,
  exerciseViewsLoading: false,
  error: null,
  subModulesError: null,
  exerciseError: null,
  exerciseViewsError: null,
  viewResultExerciseId: null,
  viewResultExerciseName: null,
  exerciseActivityData: [],
  exerciseActivityLoading: false,
  exerciseActivityError: null,
  exportExerciseActivityLoading: false,
  edxDocumentBlockId: null,
  edxDocumentName: null,
  studentAnswerExerciseQuestion: null,
  studentAnswerExerciseQuestionLoading: false,
  studentAnswerExerciseQuestionError: null,
  meetingSessionSubmoduleResult: null,
  exerciseSessionData: null,
  exerciseSessionLoading: false,
  exerciseSessionError: null,
  selectedExercise: {
    id: null,
    data: null
  }
};

const meetingSlice = createSlice({
  name: "meeting",
  initialState,
  reducers: {
    setLoading: (state, action) => {
      state.loading = action.payload;
    },
    setStudentModules: (state, action) => {
      state.studentModules = action.payload;
      state.error = null;
    },
    setError: (state, action) => {
      state.error = action.payload;
      state.loading = false;
    },
    setSubModulesLoading: (state, action) => {
      state.subModulesLoading = action.payload;
    },
    setSubModules: (state, action) => {
      state.subModules = action.payload;
      state.subModulesError = null;
    },
    setSubModulesError: (state, action) => {
      state.subModulesError = action.payload;
      state.subModulesLoading = false;
    },
    setCreatingExercise: (state, action) => {
      state.creatingExercise = action.payload;
    },
    setExerciseError: (state, action) => {
      state.exerciseError = action.payload;
      state.creatingExercise = false;
    },
    setExerciseViewsLoading: (state, action) => {
      state.exerciseViewsLoading = action.payload;
    },
    setMeetingExerciseViews: (state, action) => {
      state.meetingExerciseViews = action.payload;
      state.exerciseViewsError = null;
    },
    setExerciseViewsError: (state, action) => {
      state.exerciseViewsError = action.payload;
      state.exerciseViewsLoading = false;
    },
    setExerciseTabs: (state, action) => {
      state.exerciseTabs = action.payload;
    },
    setViewResultExerciseId: (state, action) => {
      state.viewResultExerciseId = action.payload.id;
      state.viewResultExerciseName = action.payload.name;
    },
    setExerciseActivityLoading: (state, action) => {
      state.exerciseActivityLoading = action.payload;
    },
    setExerciseActivityData: (state, action) => {
      state.exerciseActivityData = action.payload;
      state.exerciseActivityError = null;
    },
    setExerciseActivityError: (state, action) => {
      state.exerciseActivityError = action.payload;
      state.exerciseActivityLoading = false;
    },
    setExportExerciseActivityLoading: (state, action) => {
      state.exportExerciseActivityLoading = action.payload;
    },
    setEdxDocumentBlockId: (state, action) => {
      state.edxDocumentBlockId = action.payload.blockId;
      state.edxDocumentName = action.payload.name;
    },
    setSelectedExercise: (state, action) => {
      state.selectedExercise = action.payload;
    },
    setStudentAnswerExerciseQuestionLoading: (state, action) => {
      state.studentAnswerExerciseQuestionLoading = action.payload;
    },
    setStudentAnswerExerciseQuestion: (state, action) => {
      state.studentAnswerExerciseQuestion = action.payload;
      state.studentAnswerExerciseQuestionError = null;
    },
    setStudentAnswerExerciseQuestionError: (state, action) => {
      state.studentAnswerExerciseQuestionError = action.payload;
      state.studentAnswerExerciseQuestionLoading = false;
    },
    setMeetingSessionSubmoduleResult: (state, action) => {
      state.meetingSessionSubmoduleResult = action.payload;
    },
    setExerciseSessionLoading: (state, action) => {
      state.exerciseSessionLoading = action.payload;
    },
    setExerciseSessionData: (state, action) => {
      state.exerciseSessionData = action.payload;
      state.exerciseSessionError = null;
    },
    setExerciseSessionError: (state, action) => {
      state.exerciseSessionError = action.payload;
      state.exerciseSessionLoading = false;
    },
    resetMeetingExerciseData: (state) => {
      state.studentAnswerExerciseQuestion = null;
      state.meetingSessionSubmoduleResult = null;
      state.studentAnswerExerciseQuestionError = null;
      state.exerciseSessionData = null;
      state.selectedExercise = {
        id: null,
        data: null
      };
    }
  }
});

export const { 
  setLoading, 
  setStudentModules, 
  setError,
  setSubModulesLoading,
  setSubModules,
  setSubModulesError,
  setCreatingExercise,
  setExerciseError,
  setExerciseViewsLoading,
  setMeetingExerciseViews,
  setExerciseViewsError,
  setExerciseTabs,
  setViewResultExerciseId,
  setExerciseActivityLoading,
  setExerciseActivityData,
  setExerciseActivityError,
  setExportExerciseActivityLoading,
  setEdxDocumentBlockId,
  setSelectedExercise,
  setStudentAnswerExerciseQuestionLoading,
  setStudentAnswerExerciseQuestion,
  setStudentAnswerExerciseQuestionError,
  setMeetingSessionSubmoduleResult,
  setExerciseSessionLoading,
  setExerciseSessionData,
  setExerciseSessionError,
  resetMeetingExerciseData
} = meetingSlice.actions;

export default meetingSlice.reducer; 